import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Drawer,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Pagination,
  FormHelperText,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Fade,
  Snackbar,
  Badge,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Collapse,
  Paper
} from '@mui/material';
import {
  Forum,
  PostAdd,
  Close,
  FilterList,
  Reply,
  Send,
  Visibility,
  Schedule,
  ChatBubbleOutline,
  Delete,
  Edit,
  Notifications,
  NotificationsActive,
  CheckCircle,
  Error,
  Refresh,
  SearchOff,
  Add,
  MoreVert,
  PersonAdd,
  PersonRemove,
  Star,
  StarBorder,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import Search from "../../../components/common/search/Search";
import axiosInstance from "../../../utils/axios/axiosInstance";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/images/loading.gif";

const Forums = () => {
  console.log("🎯 FORUMS COMPONENT IS LOADING!");
  
  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const autoRefreshInterval = useRef(null);
  
  // State declarations
  const [threads, setThreads] = useState([]);
  const [categories] = useState([
    { 
      id: "general", 
      name: "General", 
      icon: "💬", 
      color: "#1A73E8",
      gradient: "linear-gradient(135deg, #1A73E8 0%, #4285F4 100%)",
      description: "General discussions and conversations",
      bgPattern: "linear-gradient(135deg, rgba(26, 115, 232, 0.1) 0%, rgba(66, 133, 244, 0.05) 100%)",
      hashtag: 2
    },
    { 
      id: "campus", 
      name: "Campus", 
      icon: "🏫", 
      color: "#10B981",
      gradient: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
      description: "Campus life, events, and activities",
      bgPattern: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(52, 211, 153, 0.05) 100%)",
      hashtag: 1
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingThread, setEditingThread] = useState(null);

  // Filter and Pagination states
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [searchQuery, setSearchQuery] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general'
  });

  // Form validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [validationTouched, setValidationTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [showNotificationSnackbar, setShowNotificationSnackbar] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());

  // Menu and action states
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedThreadForAction, setSelectedThreadForAction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);

  // New states for API integration
  const [loadingComments, setLoadingComments] = useState(new Set());
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [threadToEdit, setThreadToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    content: '',
    category: 'general'
  });

  // Follow states - only for threads NOT owned by current user
  const [followedThreads, setFollowedThreads] = useState(new Set());
  const [followingThreads, setFollowingThreads] = useState(new Set());

  // Comment states for inline comments
  const [expandedComments, setExpandedComments] = useState(new Set()); // Ensure comments start collapsed
  const [commentInputs, setCommentInputs] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);

  // Professional color palette matching TodoList
  const themeColors = {
    primary: '#ce55b9',
    primaryHover: '#b8479a',
    success: '#4caf50',
    error: '#f44336',
    warning: '#ff9800',
    info: '#2196f3',
    neutral: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e0e0e0',
      300: '#bdbdbd',
      400: '#9e9e9e',
      500: '#757575',
      600: '#616161',
      700: '#424242',
      800: '#212121'
    }
  };

  // Current user (replace with actual authenticated user data)
  const [currentUser] = useState({
    id: 68, // Replace with actual user ID from your auth system
    name: "Me", // Replace with actual user name from your auth system
    student_id: 68, // Replace with actual student ID from your auth system
    profile_picture: null // Replace with actual profile picture URL from your auth system
  });

  // Helper function to check if current user owns/created the thread
  const isThreadOwner = useCallback((thread) => {
    if (!thread) {
      return false;
    }
    // Use the is_owned field from API response
    return thread.is_owned === 1;
  }, []);

  // Helper function to get category hashtag for API calls
  const getCategoryHashtag = useCallback((categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.hashtag : 2;
  }, [categories]);

  // Helper function to get category hashtag (basic version)
  const getCategoryHashtagBasic = useCallback((categoryId) => {
    switch (categoryId) {
      case 'campus':
        return 1;
      case 'general':
        return 2;
      default:
        return 2;
    }
  }, []);

  // Transform API data to thread format
  const transformApiDataToThreads = useCallback((apiData) => {
    if (!Array.isArray(apiData)) {
      console.warn('API data is not an array:', apiData);
      return [];
    }

    return apiData.map(item => {
      let category = 'general';
      if (item.hashtag === 1) {
        category = 'campus';
      } else if (item.hashtag === 2) {
        category = 'general';
      }

      return {
        id: item.id,
        title: item.title || 'Untitled Discussion',
        content: item.description || 'No content available',
        category: category,
        author: {
          name: item.student?.name || 'Anonymous',
          id: item.student?.student_id || item.student_id,
          profile_picture: item.student?.profile_picture || null
        },
        replies: item.comments_count || 0,
        views: Math.floor(Math.random() * 100) + 1,
        isPinned: false,
        isActive: true,
        hasNotification: false,
        isFollowed: item.is_owned === 1 ? false : followedThreads.has(item.id),
        followersCount: item.followers_count || 0,
        createdAt: item.created_at || new Date().toISOString(),
        lastActivity: item.updated_at || item.created_at || new Date().toISOString(),
        is_owned: item.is_owned || 0,
        comments: []
      };
    });
  }, [followedThreads]);

  // Helper functions
  const getCategoryInfo = useCallback((categoryId) => {
    return categories.find(cat => cat.id === categoryId) || categories[0];
  }, [categories]);

  const formatTimeAgo = useCallback((dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return 'just now';
  }, []);

  // Fetch thread details with comments
  const fetchThreadDetails = useCallback(async (threadId) => {
    if (!isMountedRef.current || !threadId) return;
    
    setLoadingComments(prev => new Set(prev).add(threadId));
    
    try {
      console.log(`📡 Fetching thread details for ID: ${threadId}`);
      
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums/detail/${threadId}`
      );
      
      if (response.data && response.data.status === 200 && response.data.data) {
        const threadData = response.data.data;
        const comments = threadData.comments || [];
        
        // Transform comments to match expected format
        const transformedComments = comments.map(comment => ({
          id: comment.id,
          content: comment.comment,
          author: {
            name: comment.student?.name || 'Anonymous',
            id: comment.student?.student_id || comment.student_id,
            profile_picture: comment.student?.profile_picture || null
          },
          createdAt: comment.created_at || new Date().toISOString(),
          replies: []
        }));
        
        // Update thread with comments
        setThreads(prevThreads =>
          prevThreads.map(thread =>
            thread.id === threadId
              ? {
                  ...thread,
                  comments: transformedComments,
                  replies: transformedComments.length
                }
              : thread
          )
        );
        
        console.log(`✅ Loaded ${transformedComments.length} comments for thread ${threadId}`);
        
      } else {
        throw new Error(response.data?.message || 'Failed to fetch thread details');
      }
      
    } catch (error) {
      console.error('❌ Error fetching thread details:', error);
      
      let errorMessage = 'Failed to load comments';
      if (error.response?.status === 404) {
        errorMessage = 'Discussion not found or may have been deleted';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 4000,
      });
    } finally {
      setLoadingComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(threadId);
        return newSet;
      });
    }
  }, []);

  // Edit thread function
  const editThread = useCallback(async () => {
    if (!editFormData.title.trim() || !editFormData.content.trim() || !threadToEdit) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      const apiRequestData = {
        id: threadToEdit.id,
        hashtag: getCategoryHashtagBasic(editFormData.category),
        title: editFormData.title.trim(),
        description: editFormData.content.trim()
      };

      console.log('📤 Editing thread via API:', apiRequestData);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums/edit`,
        apiRequestData
      );
      
      if (response.data && response.data.status === 200) {
        // Update thread in local state
        setThreads(prevThreads =>
          prevThreads.map(thread =>
            thread.id === threadToEdit.id
              ? {
                  ...thread,
                  title: editFormData.title.trim(),
                  content: editFormData.content.trim(),
                  category: editFormData.category,
                  lastActivity: new Date().toISOString()
                }
              : thread
          )
        );
        
        setEditDialogOpen(false);
        setThreadToEdit(null);
        setEditFormData({ title: '', content: '', category: 'general' });
        
        toast.success('Discussion updated successfully! ✏️', {
          position: "top-center",
          autoClose: 3000,
        });
        
        console.log('✅ Thread edited successfully');
        
      } else {
        throw new Error(response.data?.message || 'Unexpected API response format');
      }
      
    } catch (error) {
      console.error('❌ Error editing thread:', error);
      
      let errorMessage = 'Failed to update discussion';
      if (error.response?.status === 400) {
        errorMessage = 'Invalid data. Please check your input.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to edit this discussion.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Discussion not found. It may have been deleted.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  }, [editFormData, threadToEdit, getCategoryHashtagBasic]);

  // Delete thread function
  const deleteThread = useCallback(async (threadId) => {
    if (!threadId) return;

    try {
      setSubmitting(true);
      
      console.log(`🗑️ Deleting thread ID: ${threadId}`);
      
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums/${threadId}`
      );
      
      if (response.data && response.data.status === 200) {
        // Remove thread from local state
        setThreads(prevThreads =>
          prevThreads.filter(thread => thread.id !== threadId)
        );
        
        setDeleteDialogOpen(false);
        setThreadToDelete(null);
        
        toast.success('Discussion deleted successfully! 🗑️', {
          position: "top-center",
          autoClose: 3000,
        });
        
        console.log('✅ Thread deleted successfully');
        
      } else {
        throw new Error(response.data?.message || 'Unexpected API response format');
      }
      
    } catch (error) {
      console.error('❌ Error deleting thread:', error);
      
      let errorMessage = 'Failed to delete discussion';
      if (error.response?.status === 400) {
        errorMessage = 'Invalid request. Please try again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to delete this discussion.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Discussion not found. It may have already been deleted.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  }, []);

  // Handle thread actions menu
  const handleThreadAction = useCallback((event, thread, action) => {
    event.stopPropagation();
    
    if (action === 'edit') {
      setThreadToEdit(thread);
      setEditFormData({
        title: thread.title,
        content: thread.content,
        category: thread.category
      });
      setEditDialogOpen(true);
    } else if (action === 'delete') {
      setThreadToDelete(thread);
      setDeleteDialogOpen(true);
    }
    
    setAnchorEl(null);
    setSelectedThreadForAction(null);
  }, []);

  // Fetch threads function with API integration
  const fetchThreads = useCallback(async (specificCategory = null, silent = false) => {
    if (!isMountedRef.current) return;
    
    if (!silent) setLoading(true);
    setError(null);
    
    try {
      console.log("🔄 Loading forum data...", specificCategory ? `for category: ${specificCategory}` : 'all categories');
      
      let allThreads = [];
      
      if (specificCategory && specificCategory !== 'all') {
        const hashtag = getCategoryHashtag(specificCategory);
        console.log(`📡 Fetching ${specificCategory} threads (hashtag: ${hashtag})`);
        
        const response = await axiosInstance.get(`${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums`, {
          params: { hashtag: hashtag }
        });
        
        if (response.data && response.data.status === 200) {
          const transformedData = transformApiDataToThreads(response.data.data || []);
          allThreads = transformedData;
        } else {
          throw new Error(response.data?.message || 'Invalid API response');
        }
      } else {
        try {
          const [campusResponse, generalResponse] = await Promise.all([
            axiosInstance.get(`${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums`, {
              params: { hashtag: 1 }
            }),
            axiosInstance.get(`${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums`, {
              params: { hashtag: 2 }
            })
          ]);
          
          let campusThreads = [];
          let generalThreads = [];
          
          if (campusResponse.data && campusResponse.data.status === 200) {
            campusThreads = transformApiDataToThreads(campusResponse.data.data || []);
          }
          
          if (generalResponse.data && generalResponse.data.status === 200) {
            generalThreads = transformApiDataToThreads(generalResponse.data.data || []);
          }
          
          allThreads = [...campusThreads, ...generalThreads];
          
        } catch (parallelError) {
          console.error('❌ Error in parallel API calls:', parallelError);
          throw parallelError;
        }
      }
      
      if (isMountedRef.current) {
        allThreads.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
        
        const previousCount = threads.length;
        setThreads(allThreads);
        setLastUpdateTime(new Date());
        
        if (!silent) {
          console.log('📋 Forum threads loaded:', allThreads.length, 'threads');
          
          if (allThreads.length === 0) {
            toast.info('No forum discussions found. Create your first discussion! 📝', {
              position: "top-center",
              autoClose: 3000,
            });
          } else {
            const message = allThreads.length > previousCount 
              ? `Loaded ${allThreads.length} discussions (${allThreads.length - previousCount} new)` 
              : `Loaded ${allThreads.length} forum discussions`;
            toast.success(message, {
              position: "top-center",
              autoClose: 2000,
            });
          }
        }
      }
      
    } catch (error) {
      console.error('❌ Error loading forum data:', error);
      
      if (isMountedRef.current) {
        let errorMessage = 'Failed to load forum discussions';
        
        if (error.response?.status === 400) {
          errorMessage = 'Invalid request. Please try again.';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication required. Please log in.';
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to view discussions.';
        } else if (error.response?.status === 404) {
          errorMessage = 'Forum service not found. Please contact support.';
        } else if (error.response?.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        setError(errorMessage);
        
        if (!silent) {
          toast.error(errorMessage, {
            position: "top-center",
            autoClose: 5000,
          });
        }
      }
    } finally {
      if (isMountedRef.current && !silent) {
        setLoading(false);
      }
    }
  }, [getCategoryHashtag, transformApiDataToThreads, threads.length]);

  // Toggle follow/unfollow
  const toggleFollowThread = useCallback(async (threadId, isCurrentlyFollowed) => {
    if (!isMountedRef.current || !threadId) return;

    const thread = threads.find(t => t.id === threadId);
    if (!thread) return;

    if (isThreadOwner(thread)) {
      toast.error("You cannot follow your own discussion thread.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    if (followingThreads.has(threadId)) return;

    try {
      setFollowingThreads(prev => new Set(prev).add(threadId));
      
      if (!isCurrentlyFollowed) {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums/follow`,
          { id: threadId }
        );
        
        if (response.data && response.data.status === 200) {
          setFollowedThreads(prev => {
            const newSet = new Set(prev);
            newSet.add(threadId);
            return newSet;
          });
          
          setThreads(prevThreads => 
            prevThreads.map(thread => 
              thread.id === threadId 
                ? { 
                    ...thread, 
                    isFollowed: true,
                    followersCount: (thread.followersCount || 0) + 1
                  }
                : thread
            )
          );
          
          toast.success('Following discussion! 🔔');
        }
      } else {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums/unfollow`,
          { id: threadId }
        );
        
        if (response.data && response.data.status === 200) {
          setFollowedThreads(prev => {
            const newSet = new Set(prev);
            newSet.delete(threadId);
            return newSet;
          });
          
          setThreads(prevThreads => 
            prevThreads.map(thread => 
              thread.id === threadId 
                ? { 
                    ...thread, 
                    isFollowed: false,
                    followersCount: Math.max((thread.followersCount || 1) - 1, 0)
                  }
                : thread
            )
          );
          
          toast.success('Unfollowed discussion! 👤');
        }
      }
      
    } catch (error) {
      console.error(`❌ Error ${isCurrentlyFollowed ? 'unfollowing' : 'following'} thread:`, error);
      toast.error(`Failed to ${isCurrentlyFollowed ? 'unfollow' : 'follow'} discussion`);
    } finally {
      setFollowingThreads(prev => {
        const newSet = new Set(prev);
        newSet.delete(threadId);
        return newSet;
      });
    }
  }, [threads, isThreadOwner, followingThreads]);

  // Toggle comments section with details fetch (only expand clicked thread)
  const toggleComments = useCallback((threadId) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(threadId)) {
        // Collapse this specific thread's comments
        newSet.delete(threadId);
      } else {
        // Expand this specific thread's comments
        newSet.add(threadId);
        // Fetch thread details when expanding comments for the first time
        const thread = threads.find(t => t.id === threadId);
        if (thread && (!thread.comments || thread.comments.length === 0)) {
          fetchThreadDetails(threadId);
        }
      }
      return newSet;
    });
  }, [threads, fetchThreadDetails]);

  // Add comment to thread with enhanced API integration
  const addComment = useCallback(async (threadId) => {
    const commentText = commentInputs[threadId]?.trim();
    if (!commentText) {
      toast.error('Please enter a comment before posting.');
      return;
    }

    // Basic validation
    if (commentText.length < 2) {
      toast.error('Comment must be at least 2 characters long.');
      return;
    }

    if (commentText.length > 500) {
      toast.error('Comment must be less than 500 characters.');
      return;
    }

    setSubmittingComment(true);
    try {
      console.log('📤 Adding comment via API:', { id: threadId, comment: commentText });
      
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums/comment`,
        {
          id: threadId,
          comment: commentText
        }
      );

      console.log('📥 Comment API Response:', response.data);

      if (response.data && response.data.status === 200 && response.data.data) {
        const apiData = response.data.data;
        
        // Use the student information from API response if available, otherwise fallback to currentUser
        const commentAuthor = apiData.student ? {
          name: apiData.student.name || 'Anonymous',
          id: apiData.student.student_id || apiData.student_id,
          profile_picture: apiData.student.profile_picture || null
        } : {
          name: currentUser.name,
          id: currentUser.student_id,
          profile_picture: currentUser.profile_picture || null
        };
        
        const newComment = {
          id: apiData.id,
          content: apiData.comment,
          author: commentAuthor,
          createdAt: apiData.created_at || new Date().toISOString(),
          replies: []
        };

        // Update the thread with new comment
        setThreads(prevThreads =>
          prevThreads.map(t =>
            t.id === threadId ? {
              ...t,
              comments: [...(t.comments || []), newComment],
              replies: (t.replies || 0) + 1
            } : t
          )
        );

        // Clear the comment input
        setCommentInputs(prev => ({ ...prev, [threadId]: '' }));
        
        toast.success(response.data.message || "Comment posted successfully! 💬", {
          position: "top-center",
          autoClose: 2000,
        });

        console.log('✅ Comment added successfully:', newComment);
      } else {
        throw new Error(response.data?.message || 'Unexpected API response format');
      }
    } catch (error) {
      console.error('❌ Error posting comment:', error);
      
      let errorMessage = 'Failed to post comment';
      
      if (error.response?.status === 400) {
        errorMessage = 'Invalid comment data. Please check your input.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to comment.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Discussion not found. It may have been deleted.';
      } else if (error.response?.status === 422) {
        errorMessage = 'Validation failed. Please check your comment.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 4000,
      });
    } finally {
      setSubmittingComment(false);
    }
  }, [commentInputs, currentUser]);

  // Create thread (keeping existing validation and logic)
  const createThread = useCallback(async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      const apiRequestData = {
        hashtag: getCategoryHashtagBasic(formData.category),
        title: formData.title.trim(),
        description: formData.content.trim()
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/forums`,
        apiRequestData
      );
      
      if (response.data && response.data.status === 200 && response.data.data) {
        const apiData = response.data.data;
        
        const newThread = {
          id: apiData.id,
          title: apiData.title,
          content: apiData.description,
          category: apiData.hashtag === 1 ? 'campus' : 'general',
          author: { 
            name: currentUser.name,
            id: currentUser.student_id,
            profile_picture: currentUser.profile_picture || null
          },
          replies: 0,
          views: Math.floor(Math.random() * 10) + 1,
          isPinned: false,
          isActive: true,
          hasNotification: false,
          isFollowed: false,
          followersCount: 0,
          createdAt: apiData.created_at || new Date().toISOString(),
          lastActivity: apiData.updated_at || new Date().toISOString(),
          is_owned: 1, // New thread is always owned by current user
          comments: []
        };

        setThreads(prev => [newThread, ...prev]);
        setOpen(false);
        setFormData({ title: '', content: '', category: 'general' });
        
        toast.success('Discussion created successfully! 🎉');
        
      } else {
        throw new Error(response.data?.message || 'Unexpected API response format');
      }
      
    } catch (error) {
      console.error('❌ Error creating thread:', error);
      toast.error('Failed to create discussion');
    } finally {
      setSubmitting(false);
    }
  }, [formData, getCategoryHashtagBasic, currentUser]);

  // Load data on component mount
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Handle search functionality
  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  // Filter and sort threads
  const getFilteredAndSortedThreads = useCallback(() => {
    let filteredThreads = [...threads];

    if (categoryFilter !== 'all') {
      filteredThreads = filteredThreads.filter(thread => thread.category === categoryFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredThreads = filteredThreads.filter(thread =>
        thread.title?.toLowerCase().includes(query) ||
        thread.content?.toLowerCase().includes(query) ||
        thread.author?.name?.toLowerCase().includes(query)
      );
    }

    filteredThreads.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));

    return filteredThreads;
  }, [threads, categoryFilter, searchQuery]);

  // Get paginated threads
  const getPaginatedThreads = useCallback(() => {
    const filteredThreads = getFilteredAndSortedThreads();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredThreads.slice(startIndex, endIndex);
  }, [getFilteredAndSortedThreads, currentPage, itemsPerPage]);

  // Calculate total pages
  const filteredAndSortedThreads = getFilteredAndSortedThreads();
  const totalPages = Math.ceil(filteredAndSortedThreads.length / itemsPerPage);

  // Handle page change
  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Early returns for loading/error states
  if (loading) {
    return (
      <Grid container spacing={2}>
        <div className="container">
          <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
            <div className="top-sec">
              <div className="card custom-card">
                <div className="card-body">
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh'
                  }}>
                    {loadingGif ? (
                      <img
                        src={loadingGif}
                        alt="Loading..."
                        style={{ width: '80px' }}
                      />
                    ) : (
                      <CircularProgress size={60} />
                    )}
                    <Typography variant="body1" sx={{ mt: 2, fontFamily: '"Saira", sans-serif' }}>
                      Loading forum discussions...
                    </Typography>
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Grid>
    );
  }

  if (error && !threads.length) {
    return (
      <Grid container spacing={2}>
        <div className="container">
          <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
            <div className="top-sec">
              <div className="card custom-card">
                <div className="card-body">
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Alert severity="error" sx={{ mb: 2, fontFamily: '"Saira", sans-serif' }}>
                      <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                        <strong>Error!</strong> {error}
                      </Typography>
                    </Alert>
                    <Button
                      variant="contained"
                      onClick={() => fetchThreads()}
                      sx={{
                        mt: 2,
                        fontFamily: '"Saira", sans-serif',
                        background: themeColors.primary,
                        '&:hover': {
                          background: themeColors.primaryHover,
                        }
                      }}
                    >
                      Retry
                    </Button>
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Grid>
    );
  }

  const paginatedThreads = getPaginatedThreads();

  return (
    <>
      <Grid container spacing={2}>
        <div className="container">
          <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
            <div className="top-sec">
              <div className="card custom-card">
                <div className="card-body">

                  {/* Header */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                    <div className="d-flex align-items-center">
                      <h3 className="mb-2" style={{ fontFamily: '"Saira", sans-serif' }}>Community Forum</h3>
                    </div>
                    <div className="d-flex align-items-center flex-wrap mb-1">
                     
                      
                     
                      
                      <Button 
                        variant="contained" 
                        color="primary" 
                        className="!w-fit !px-3 !h-10"
                        onClick={() => setOpen(true)}
                        startIcon={<PostAdd />}
                        sx={{
                          background: themeColors.primary,
                          fontFamily: '"Saira", sans-serif',
                          textTransform: 'none',
                          '&:hover': {
                            background: themeColors.primaryHover
                          }
                        }}
                      >
                        Start Discussion
                      </Button>
                    </div>
                  </div>

                  {/* Update info */}
                 

                  {/* Categories Dashboard */}
                  <div className="row mb-4">
                    {categories.map((category) => (
                      <div key={category.id} className="col-md-6">
                        <Card 
                          sx={{
                            borderRadius: '16px',
                            background: category.bgPattern,
                            border: `1px solid ${category.color}30`,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontFamily: '"Saira", sans-serif',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 25px rgba(0,0,0,0.1)',
                              border: `1px solid ${category.color}60`
                            }
                          }}
                          onClick={() => {
                            setCategoryFilter(category.id);
                            setCurrentPage(1);
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '12px',
                                background: category.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.5rem'
                              }}>
                                {category.icon}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 700, 
                                  color: category.color,
                                  fontFamily: '"Saira", sans-serif'
                                }}>
                                  {category.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{
                                  fontFamily: '"Saira", sans-serif'
                                }}>
                                  {category.description}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  color: category.color, 
                                  fontWeight: 600,
                                  fontFamily: '"Saira", sans-serif'
                                }}>
                                  {threads.filter(t => t.category === category.id).length} discussions
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>

                  {/* Filter Panel */}
                  <div className="row filter-panel mt-4">
                    <div className="col-md-8">
                      <div className="d-flex align-items-center gap-2">
                        <Search
                          value={searchQuery}
                          handleInputChange={(e) => handleSearch(e.target.value)}
                          placeholder="Search discussions..."
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="w-full flex items-center justify-end gap-2">
                        <FormControl
                          size="small"
                          sx={{
                            minWidth: 160,
                            '& .MuiOutlinedInput-root': {
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: themeColors.primary,
                              }
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: themeColors.primary
                            }
                          }}
                        >
                          <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>
                            Category
                          </InputLabel>
                          <Select
                            value={categoryFilter}
                            label="Category"
                            onChange={(e) => {
                              setCategoryFilter(e.target.value);
                              setCurrentPage(1);
                            }}
                            sx={{
                              fontFamily: '"Saira", sans-serif',
                              fontWeight: 500,
                              '& .MuiSelect-select': {
                                fontFamily: '"Saira", sans-serif'
                              }
                            }}
                          >
                            <MenuItem value="all" sx={{ fontFamily: '"Saira", sans-serif' }}>
                              All Categories ({threads.length})
                            </MenuItem>
                            {categories.map((category) => (
                              <MenuItem
                                key={category.id}
                                value={category.id}
                                sx={{ fontFamily: '"Saira", sans-serif' }}
                              >
                                {category.icon} {category.name} ({threads.filter(t => t.category === category.id).length})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                  </div>

                  {/* Error display */}
                  {error && threads.length > 0 && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        fontFamily: '"Saira", sans-serif'
                      }}
                      onClose={() => setError(null)}
                    >
                      <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                        {error}
                      </Typography>
                    </Alert>
                  )}

                  {/* List View Threads */}
                  <Box sx={{ mt: 4 }}>
                    {paginatedThreads.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {paginatedThreads.map((thread, index) => {
                          const categoryInfo = getCategoryInfo(thread.category);
                          const userOwnsThread = isThreadOwner(thread);
                          const isCommentsExpanded = expandedComments.has(thread.id);
                          
                          return (
                            <Fade in={true} style={{ transitionDelay: `${index * 50}ms` }} key={thread.id}>
                              <Card
                                sx={{
                                  borderRadius: 4,
                                  border: '1px solid #e5e7eb',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  fontFamily: '"Saira", sans-serif',
                                  background: 'linear-gradient(to bottom, #ffffff, #fefefe)',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 3,
                                    background: categoryInfo.gradient,
                                    opacity: 0.8
                                  },
                                  '&:hover': {
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    transform: 'translateY(-2px)',
                                    border: `1px solid ${categoryInfo.color}40`,
                                    '&::before': {
                                      height: 4,
                                      opacity: 1
                                    }
                                  }
                                }}
                              >
                                <CardContent sx={{ p: 4 }}>
                                  {/* Post Header */}
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
                                    {/* Profile Picture */}
                                    <Box sx={{ position: 'relative' }}>
                                      <Avatar 
                                        src={thread.author?.profile_picture}
                                        sx={{
                                          width: 56,
                                          height: 56,
                                          background: thread.author?.profile_picture ? 'transparent' : categoryInfo.gradient,
                                          fontWeight: 700,
                                          fontSize: '1.4rem',
                                          fontFamily: '"Saira", sans-serif',
                                          border: '3px solid white',
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                      >
                                        {!thread.author?.profile_picture && (thread.author?.name?.charAt(0) || 'U')}
                                      </Avatar>
                                      {/* Online indicator for demonstration */}
                                      <Box sx={{
                                        position: 'absolute',
                                        bottom: 2,
                                        right: 2,
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        background: themeColors.success,
                                        border: '2px solid white',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                      }} />
                                    </Box>
                                    
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="h6" sx={{
                                          fontWeight: 700,
                                          fontFamily: '"Saira", sans-serif',
                                          fontSize: '1.1rem',
                                          color: '#1f2937',
                                          flex: 1,
                                          mr: 2
                                        }}>
                                          {thread.author?.name || 'Anonymous'}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {userOwnsThread && (
                                            <Chip 
                                              label="Author"
                                              size="small"
                                              sx={{
                                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                                color: 'white',
                                                fontFamily: '"Saira", sans-serif',
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                height: 22,
                                                '& .MuiChip-label': {
                                                  px: 1
                                                }
                                              }}
                                            />
                                          )}
                                          
                                          {/* Beautiful Follow Button with Better Padding */}
                                          {!userOwnsThread && (
                                            <Button
                                              size="small"
                                              variant={thread.isFollowed ? "outlined" : "contained"}
                                              onClick={() => toggleFollowThread(thread.id, thread.isFollowed)}
                                              disabled={followingThreads.has(thread.id)}
                                              sx={{
                                                textTransform: 'none',
                                                fontSize: '0.7rem',
                                                fontFamily: '"Saira", sans-serif',
                                                fontWeight: 600,
                                                py: 0.6,
                                                px: 1.5,
                                                minWidth: 'auto',
                                                height: 'auto',
                                                borderRadius: 2,
                                                background: thread.isFollowed 
                                                  ? 'transparent' 
                                                  : `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.primaryHover} 100%)`,
                                                color: thread.isFollowed ? themeColors.primary : 'white',
                                                borderColor: themeColors.primary,
                                                borderWidth: '1.5px',
                                                boxShadow: thread.isFollowed 
                                                  ? 'none' 
                                                  : '0 2px 8px rgba(206, 85, 185, 0.25)',
                                                '&:hover': {
                                                  background: thread.isFollowed 
                                                    ? `linear-gradient(135deg, ${themeColors.primary}10, ${themeColors.primary}05)` 
                                                    : `linear-gradient(135deg, ${themeColors.primaryHover} 0%, ${themeColors.primary} 100%)`,
                                                  transform: 'translateY(-1px)',
                                                  boxShadow: thread.isFollowed 
                                                    ? '0 2px 12px rgba(206, 85, 185, 0.15)' 
                                                    : '0 4px 16px rgba(206, 85, 185, 0.35)',
                                                  borderColor: thread.isFollowed ? themeColors.primaryHover : themeColors.primary
                                                },
                                                '&:active': {
                                                  transform: 'translateY(0px)',
                                                  boxShadow: thread.isFollowed 
                                                    ? '0 1px 4px rgba(206, 85, 185, 0.2)' 
                                                    : '0 2px 8px rgba(206, 85, 185, 0.3)'
                                                },
                                                '&:disabled': {
                                                  opacity: 0.7,
                                                  transform: 'none',
                                                  boxShadow: 'none'
                                                },
                                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::before': {
                                                  content: '""',
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: '-100%',
                                                  width: '100%',
                                                  height: '100%',
                                                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                                  transition: 'left 0.5s',
                                                },
                                                '&:hover::before': {
                                                  left: '100%'
                                                }
                                              }}
                                            >
                                              {followingThreads.has(thread.id) ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <CircularProgress size={10} color="inherit" />
                                                  <Typography sx={{ fontSize: '0.7rem', fontFamily: '"Saira", sans-serif' }}>
                                                    {thread.isFollowed ? 'Unfollowing...' : 'Following...'}
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  {thread.isFollowed ? (
                                                    <>
                                                      <PersonRemove sx={{ fontSize: 12 }} />
                                                      <Typography sx={{ fontSize: '0.7rem', fontFamily: '"Saira", sans-serif' }}>
                                                        Unfollow
                                                      </Typography>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <PersonAdd sx={{ fontSize: 12 }} />
                                                      <Typography sx={{ fontSize: '0.7rem', fontFamily: '"Saira", sans-serif' }}>
                                                        Follow
                                                      </Typography>
                                                    </>
                                                  )}
                                                </Box>
                                              )}
                                            </Button>
                                          )}

                                          {/* Thread Actions Menu for Owner */}
                                          {userOwnsThread && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setAnchorEl(e.currentTarget);
                                                setSelectedThreadForAction(thread);
                                              }}
                                              sx={{
                                                color: '#6b7280',
                                                '&:hover': {
                                                  color: themeColors.primary,
                                                  backgroundColor: `${themeColors.primary}08`
                                                }
                                              }}
                                            >
                                              <MoreVert sx={{ fontSize: 18 }} />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Box>
                                      
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{
                                          fontFamily: '"Saira", sans-serif',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          fontSize: '0.8rem',
                                          fontWeight: 500
                                        }}>
                                          <Schedule sx={{ fontSize: 14, opacity: 0.7 }} />
                                          {formatTimeAgo(thread.createdAt)}
                                        </Typography>
                                        
                                        <Chip
                                          label={`${categoryInfo.icon} ${categoryInfo.name}`}
                                          size="small"
                                          sx={{
                                            background: `linear-gradient(135deg, ${categoryInfo.color}15, ${categoryInfo.color}05)`,
                                            color: categoryInfo.color,
                                            border: `1px solid ${categoryInfo.color}20`,
                                            fontFamily: '"Saira", sans-serif',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            height: 22,
                                            '& .MuiChip-label': {
                                              px: 1.5
                                            }
                                          }}
                                        />
                                        
                                        {thread.followersCount > 0 && (
                                          <Chip
                                            label={`${thread.followersCount} follower${thread.followersCount > 1 ? 's' : ''}`}
                                            size="small"
                                            sx={{
                                              background: '#f3f4f6',
                                              color: '#6b7280',
                                              border: '1px solid #e5e7eb',
                                              fontFamily: '"Saira", sans-serif',
                                              fontSize: '0.7rem',
                                              height: 20
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>

                                  {/* Post Content */}
                                  <Box sx={{ mb: 4, pl: 1 }}>
                                    <Typography variant="h5" sx={{
                                      fontWeight: 700,
                                      fontFamily: '"Saira", sans-serif',
                                      mb: 2,
                                      fontSize: '1.3rem',
                                      lineHeight: 1.4,
                                      color: '#111827',
                                      letterSpacing: '-0.025em'
                                    }}>
                                      {thread.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{
                                      fontFamily: '"Saira", sans-serif',
                                      lineHeight: 1.7,
                                      color: '#4b5563',
                                      fontSize: '0.95rem',
                                      letterSpacing: '0.01em'
                                    }}>
                                      {thread.content}
                                    </Typography>
                                  </Box>

                                  {/* Post Actions */}
                                  <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    py: 2,
                                    px: 1,
                                    borderTop: '1px solid #f3f4f6',
                                    borderRadius: 2,
                                    background: 'linear-gradient(to right, #fafbfc, #ffffff)'
                                  }}>
                                    <Button
                                      size="small"
                                      startIcon={<Visibility sx={{ fontSize: 16 }} />}
                                      sx={{
                                        color: '#6b7280',
                                        textTransform: 'none',
                                        fontFamily: '"Saira", sans-serif',
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        '&:hover': {
                                          backgroundColor: `${themeColors.info}08`,
                                          color: themeColors.info
                                        }
                                      }}
                                    >
                                      {thread.views} View{thread.views !== 1 ? 's' : ''}
                                    </Button>
                                    
                                    <Button
                                      size="small"
                                      startIcon={isCommentsExpanded ? <ExpandLess /> : <ExpandMore />}
                                      onClick={() => toggleComments(thread.id)}
                                      sx={{
                                        color: '#6b7280',
                                        textTransform: 'none',
                                        fontFamily: '"Saira", sans-serif',
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        '&:hover': {
                                          backgroundColor: `${themeColors.primary}08`,
                                          color: themeColors.primary
                                        }
                                      }}
                                    >
                                      {thread.replies} Comment{thread.replies !== 1 ? 's' : ''}
                                    </Button>
                                    
                                    <Box sx={{ flex: 1 }} />
                                    
                                    <Typography variant="caption" sx={{
                                      color: '#9ca3af',
                                      fontFamily: '"Saira", sans-serif',
                                      fontSize: '0.75rem',
                                      fontStyle: 'italic'
                                    }}>
                                      {isCommentsExpanded ? 'Click to hide comments' : 'Click to view discussion'}
                                    </Typography>
                                  </Box>

                                  {/* Comments Section */}
                                  <Collapse in={isCommentsExpanded}>
                                    <Divider sx={{ my: 3, opacity: 0.7 }} />
                                    
                                    {/* Enhanced Add Comment Section */}
                                    <Box sx={{ 
                                      display: 'flex', 
                                      gap: 2, 
                                      mb: 3, 
                                      px: 1,
                                      py: 2,
                                      backgroundColor: '#f8fafc',
                                      borderRadius: 3,
                                      border: '1px solid #e2e8f0'
                                    }}>
                                      <Avatar 
                                        src={currentUser?.profile_picture}
                                        sx={{ 
                                          width: 40, 
                                          height: 40,
                                          border: '2px solid #ffffff',
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                      >
                                        {currentUser?.name?.charAt(0) || 'U'}
                                      </Avatar>
                                      <Box sx={{ flex: 1 }}>
                                        <TextField
                                          fullWidth
                                          multiline
                                          rows={2}
                                          placeholder="Share your thoughts on this discussion..."
                                          value={commentInputs[thread.id] || ''}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.length <= 500) {
                                              setCommentInputs(prev => ({
                                                ...prev,
                                                [thread.id]: value
                                              }));
                                            }
                                          }}
                                          disabled={submittingComment}
                                          error={commentInputs[thread.id] && commentInputs[thread.id].trim().length > 0 && commentInputs[thread.id].trim().length < 2}
                                          sx={{
                                            mb: 1,
                                            '& .MuiOutlinedInput-root': {
                                              borderRadius: 2,
                                              backgroundColor: '#ffffff',
                                              fontFamily: '"Saira", sans-serif',
                                              fontSize: '0.9rem',
                                              border: '1.5px solid #e5e7eb',
                                              '&:hover': {
                                                borderColor: '#d1d5db',
                                                backgroundColor: '#ffffff'
                                              },
                                              '&.Mui-focused': {
                                                backgroundColor: '#ffffff',
                                                borderColor: themeColors.primary,
                                                boxShadow: `0 0 0 3px ${themeColors.primary}15`
                                              },
                                              '&.Mui-error': {
                                                borderColor: themeColors.error,
                                                '&.Mui-focused': {
                                                  boxShadow: `0 0 0 3px ${themeColors.error}15`
                                                }
                                              }
                                            },
                                            '& .MuiInputBase-input': {
                                              lineHeight: 1.5
                                            }
                                          }}
                                        />
                                        
                                        {/* Character count and actions */}
                                        <Box sx={{ 
                                          display: 'flex', 
                                          justifyContent: 'space-between', 
                                          alignItems: 'center',
                                          mt: 1
                                        }}>
                                          <Typography variant="caption" sx={{
                                            color: (commentInputs[thread.id]?.length || 0) > 450 
                                              ? themeColors.error 
                                              : (commentInputs[thread.id]?.length || 0) > 400 
                                              ? themeColors.warning 
                                              : '#9ca3af',
                                            fontFamily: '"Saira", sans-serif',
                                            fontSize: '0.75rem'
                                          }}>
                                            {commentInputs[thread.id]?.length || 0}/500 characters
                                            {commentInputs[thread.id] && commentInputs[thread.id].trim().length > 0 && commentInputs[thread.id].trim().length < 2 && (
                                              <Typography component="span" sx={{ color: themeColors.error, ml: 1 }}>
                                                • Minimum 2 characters required
                                              </Typography>
                                            )}
                                          </Typography>
                                          
                                          <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={submittingComment ? 
                                              <CircularProgress size={14} color="inherit" /> : 
                                              <Send sx={{ fontSize: 14 }} />
                                            }
                                            onClick={() => addComment(thread.id)}
                                            disabled={
                                              !commentInputs[thread.id]?.trim() || 
                                              commentInputs[thread.id]?.trim().length < 2 ||
                                              submittingComment
                                            }
                                            sx={{
                                              textTransform: 'none',
                                              fontFamily: '"Saira", sans-serif',
                                              fontWeight: 600,
                                              fontSize: '0.8rem',
                                              py: 0.75,
                                              px: 2,
                                              borderRadius: 2,
                                              background: `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.primaryHover})`,
                                              boxShadow: '0 2px 8px rgba(206, 85, 185, 0.25)',
                                              '&:hover': {
                                                background: `linear-gradient(135deg, ${themeColors.primaryHover}, ${themeColors.primary})`,
                                                boxShadow: '0 4px 12px rgba(206, 85, 185, 0.35)',
                                                transform: 'translateY(-1px)'
                                              },
                                              '&:active': {
                                                transform: 'translateY(0px)'
                                              },
                                              '&:disabled': {
                                                background: '#e5e7eb',
                                                color: '#9ca3af',
                                                boxShadow: 'none',
                                                transform: 'none'
                                              },
                                              transition: 'all 0.2s ease'
                                            }}
                                          >
                                            {submittingComment ? 'Posting...' : 'Post Comment'}
                                          </Button>
                                        </Box>
                                      </Box>
                                    </Box>

                                    {/* Enhanced Comments List with Loading */}
                                    {loadingComments.has(thread.id) && (
                                      <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        py: 4,
                                        gap: 2
                                      }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{
                                          fontFamily: '"Saira", sans-serif',
                                          color: '#6b7280'
                                        }}>
                                          Loading comments...
                                        </Typography>
                                      </Box>
                                    )}
                                    
                                    {!loadingComments.has(thread.id) && thread.comments && thread.comments.length > 0 && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 1 }}>
                                        {thread.comments.map((comment, commentIndex) => (
                                          <Fade in={true} style={{ transitionDelay: `${commentIndex * 100}ms` }} key={comment.id}>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                              <Avatar 
                                                src={comment.author?.profile_picture}
                                                sx={{ 
                                                  width: 36, 
                                                  height: 36,
                                                  border: '2px solid #ffffff',
                                                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                                }}
                                              >
                                                {comment.author?.name?.charAt(0) || 'U'}
                                              </Avatar>
                                              <Paper sx={{
                                                flex: 1,
                                                p: 2.5,
                                                backgroundColor: '#ffffff',
                                                borderRadius: 3,
                                                border: '1px solid #e2e8f0',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                                transition: 'all 0.3s ease',
                                                position: 'relative',
                                                '&:hover': {
                                                  backgroundColor: '#f8fafc',
                                                  borderColor: '#cbd5e1',
                                                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                                  transform: 'translateY(-1px)'
                                                },
                                                '&::before': {
                                                  content: '""',
                                                  position: 'absolute',
                                                  top: '50%',
                                                  left: -6,
                                                  transform: 'translateY(-50%)',
                                                  width: 0,
                                                  height: 0,
                                                  borderTop: '6px solid transparent',
                                                  borderBottom: '6px solid transparent',
                                                  borderRight: '6px solid #e2e8f0',
                                                  transition: 'border-right-color 0.3s ease'
                                                },
                                                '&:hover::before': {
                                                  borderRightColor: '#cbd5e1'
                                                }
                                              }}>
                                                {/* Comment Header */}
                                                <Box sx={{ 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  justifyContent: 'space-between',
                                                  mb: 1.5
                                                }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2" sx={{
                                                      fontWeight: 700,
                                                      fontFamily: '"Saira", sans-serif',
                                                      color: '#1f2937',
                                                      fontSize: '0.9rem'
                                                    }}>
                                                      {comment.author?.name || 'Anonymous'}
                                                    </Typography>
                                                    {comment.author?.id === currentUser?.student_id && (
                                                      <Chip 
                                                        label="You"
                                                        size="small"
                                                        sx={{
                                                          height: 18,
                                                          fontSize: '0.65rem',
                                                          fontWeight: 600,
                                                          background: `${themeColors.primary}15`,
                                                          color: themeColors.primary,
                                                          border: `1px solid ${themeColors.primary}30`,
                                                          fontFamily: '"Saira", sans-serif'
                                                        }}
                                                      />
                                                    )}
                                                  </Box>
                                                  <Typography variant="caption" sx={{
                                                    fontFamily: '"Saira", sans-serif',
                                                    fontSize: '0.75rem',
                                                    color: '#6b7280',
                                                    fontWeight: 500
                                                  }}>
                                                    {formatTimeAgo(comment.createdAt)}
                                                  </Typography>
                                                </Box>

                                                {/* Comment Content */}
                                                <Typography variant="body2" sx={{
                                                  fontFamily: '"Saira", sans-serif',
                                                  mb: 2,
                                                  lineHeight: 1.6,
                                                  color: '#374151',
                                                  fontSize: '0.9rem'
                                                }}>
                                                  {comment.content}
                                                </Typography>

                                                {/* Comment Actions */}
                                                <Box sx={{ 
                                                  display: 'flex', 
                                                  alignItems: 'center', 
                                                  gap: 3,
                                                  pt: 1,
                                                  borderTop: '1px solid #f3f4f6'
                                                }}>
                                                  <Button
                                                    size="small"
                                                    startIcon={<Reply sx={{ fontSize: 14 }} />}
                                                    sx={{
                                                      minWidth: 'auto',
                                                      py: 0.5,
                                                      px: 1,
                                                      color: '#6b7280',
                                                      fontSize: '0.75rem',
                                                      fontFamily: '"Saira", sans-serif',
                                                      fontWeight: 500,
                                                      textTransform: 'none',
                                                      borderRadius: 1.5,
                                                      '&:hover': {
                                                        color: themeColors.primary,
                                                        backgroundColor: `${themeColors.primary}08`
                                                      }
                                                    }}
                                                  >
                                                    Reply
                                                  </Button>
                                                  
                                                  <Typography variant="caption" sx={{
                                                    color: '#9ca3af',
                                                    fontFamily: '"Saira", sans-serif',
                                                    fontSize: '0.7rem',
                                                    fontStyle: 'italic'
                                                  }}>
                                                    Comment #{commentIndex + 1}
                                                  </Typography>
                                                </Box>
                                              </Paper>
                                            </Box>
                                          </Fade>
                                        ))}
                                      </Box>
                                    )}

                                    {!loadingComments.has(thread.id) && (!thread.comments || thread.comments.length === 0) && (
                                      <Box sx={{
                                        textAlign: 'center',
                                        py: 4,
                                        color: 'text.secondary',
                                        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                                        borderRadius: 3,
                                        border: '1px dashed #e2e8f0'
                                      }}>
                                        <ChatBubbleOutline sx={{ fontSize: 32, mb: 1, opacity: 0.4, color: '#94a3b8' }} />
                                        <Typography variant="body2" sx={{ 
                                          fontFamily: '"Saira", sans-serif',
                                          color: '#64748b',
                                          fontWeight: 500
                                        }}>
                                          No comments yet. Start the conversation!
                                        </Typography>
                                      </Box>
                                    )}
                                  </Collapse>
                                </CardContent>
                              </Card>
                            </Fade>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{
                        textAlign: 'center',
                        py: 6,
                        px: 4,
                        backgroundColor: '#fafafa',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0'
                      }}>
                        <SearchOff sx={{
                          fontSize: 48,
                          color: '#bdbdbd',
                          mb: 2
                        }} />
                        <Typography variant="h6" sx={{
                          mb: 1,
                          fontWeight: 600,
                          fontFamily: '"Saira", sans-serif',
                          color: '#424242'
                        }}>
                          No discussions found
                        </Typography>
                        <Typography variant="body2" sx={{
                          mb: 4,
                          color: '#757575',
                          fontFamily: '"Saira", sans-serif',
                          maxWidth: 400,
                          mx: 'auto',
                          lineHeight: 1.5
                        }}>
                          {(categoryFilter !== 'all' || searchQuery.trim()) ?
                            'No discussions match your current filter criteria. Try adjusting your filters or create a new discussion.' :
                            'You haven\'t created any discussions yet. Get started by creating your first discussion.'
                          }
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => setOpen(true)}
                          sx={{
                            backgroundColor: themeColors.primary,
                            fontFamily: '"Saira", sans-serif',
                            '&:hover': {
                              backgroundColor: themeColors.primaryHover
                            }
                          }}
                        >
                          New Discussion
                        </Button>
                      </Box>
                    )}
                  </Box>

                </div>
              </div>

              {/* Pagination Info */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 3,
                mb: 2
              }}>
                <Typography variant="body2" sx={{
                  color: '#6c757d',
                  fontFamily: '"Saira", sans-serif',
                  textAlign: 'center'
                }}>
                  Showing {paginatedThreads.length} of {filteredAndSortedThreads.length} discussions
                  {categoryFilter !== 'all' && (
                    <span> • Category: {getCategoryInfo(categoryFilter).name}</span>
                  )}
                  {searchQuery.trim() && (
                    <span> • Search: "{searchQuery}"</span>
                  )}
                  {filteredAndSortedThreads.length > 0 && <span> • Sorted by activity (newest first)</span>}
                </Typography>
              </Box>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 4
                }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontFamily: '"Saira", sans-serif',
                        fontWeight: 600,
                        '&.Mui-selected': {
                          backgroundColor: themeColors.primary,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: themeColors.primaryHover,
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}

            </div>
          </div>
        </div>
      </Grid>

      {/* Thread Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setSelectedThreadForAction(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            mt: 1
          }
        }}
      >
        <MenuItem
          onClick={(e) => handleThreadAction(e, selectedThreadForAction, 'edit')}
          sx={{
            fontFamily: '"Saira", sans-serif',
            fontSize: '0.9rem',
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: `${themeColors.primary}08`
            }
          }}
        >
          <Edit sx={{ fontSize: 18, mr: 2, color: themeColors.primary }} />
          Edit Discussion
        </MenuItem>
        <MenuItem
          onClick={(e) => handleThreadAction(e, selectedThreadForAction, 'delete')}
          sx={{
            fontFamily: '"Saira", sans-serif',
            fontSize: '0.9rem',
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: `${themeColors.error}08`
            }
          }}
        >
          <Delete sx={{ fontSize: 18, mr: 2, color: themeColors.error }} />
          Delete Discussion
        </MenuItem>
      </Menu>

      {/* Edit Discussion Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !submitting && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: '"Saira", sans-serif'
          }
        }}
      >
        <DialogTitle sx={{
          background: themeColors.primary,
          color: 'white',
          fontFamily: '"Saira", sans-serif',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Edit />
          Edit Discussion
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          {/* Category Selection */}
          <FormControl fullWidth sx={{ mb: 3, mt: 1 }}>
            <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Category</InputLabel>
            <Select
              value={editFormData.category}
              label="Category"
              onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
              disabled={submitting}
              sx={{ fontFamily: '"Saira", sans-serif' }}
            >
              {categories.map((category) => (
                <MenuItem
                  key={category.id}
                  value={category.id}
                  sx={{ fontFamily: '"Saira", sans-serif' }}
                >
                  {category.icon} {category.name} - {category.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Title */}
          <TextField
            fullWidth
            label="Discussion Title"
            value={editFormData.title}
            onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
            required
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': { fontFamily: '"Saira", sans-serif' },
              '& .MuiOutlinedInput-root': { fontFamily: '"Saira", sans-serif' }
            }}
            disabled={submitting}
          />

          {/* Content */}
          <TextField
            fullWidth
            label="Discussion Description"
            multiline
            rows={6}
            value={editFormData.content}
            onChange={(e) => setEditFormData(prev => ({ ...prev, content: e.target.value }))}
            required
            sx={{
              '& .MuiInputLabel-root': { fontFamily: '"Saira", sans-serif' },
              '& .MuiOutlinedInput-root': { fontFamily: '"Saira", sans-serif' }
            }}
            disabled={submitting}
          />
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={submitting}
            sx={{ fontFamily: '"Saira", sans-serif' }}
          >
            Cancel
          </Button>
          <Button
            onClick={editThread}
            variant="contained"
            disabled={submitting || !editFormData.title.trim() || !editFormData.content.trim()}
            sx={{
              background: themeColors.primary,
              fontFamily: '"Saira", sans-serif',
              '&:hover': { background: themeColors.primaryHover }
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                Updating...
              </Box>
            ) : (
              'Update Discussion'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !submitting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: '"Saira", sans-serif'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: '"Saira", sans-serif',
          fontWeight: 600,
          color: themeColors.error,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Delete />
          Delete Discussion
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText sx={{
            fontFamily: '"Saira", sans-serif',
            fontSize: '1rem',
            mb: 2
          }}>
            Are you sure you want to delete this discussion? This action cannot be undone.
          </DialogContentText>
          
          {threadToDelete && (
            <Box sx={{
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: 2,
              border: '1px solid #e9ecef'
            }}>
              <Typography variant="subtitle2" sx={{
                fontFamily: '"Saira", sans-serif',
                fontWeight: 600,
                mb: 1
              }}>
                "{threadToDelete.title}"
              </Typography>
              <Typography variant="body2" sx={{
                fontFamily: '"Saira", sans-serif',
                color: '#6c757d',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {threadToDelete.content}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={submitting}
            sx={{ fontFamily: '"Saira", sans-serif' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => threadToDelete && deleteThread(threadToDelete.id)}
            variant="contained"
            disabled={submitting}
            sx={{
              background: themeColors.error,
              fontFamily: '"Saira", sans-serif',
              '&:hover': { background: '#d32f2f' }
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                Deleting...
              </Box>
            ) : (
              'Delete Discussion'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Discussion Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400, md: 450 },
            maxWidth: '90vw',
            fontFamily: '"Saira", sans-serif'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{
            background: themeColors.primary,
            color: 'white',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PostAdd />
              <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: '"Saira", sans-serif' }}>
                Start New Discussion
              </Typography>
            </Box>
            <IconButton
              onClick={() => setOpen(false)}
              disabled={submitting}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{
            flex: 1,
            p: 3,
            overflow: 'auto',
            background: '#ffffff'
          }}>
            {/* Category Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                disabled={submitting}
                sx={{ fontFamily: '"Saira", sans-serif' }}
              >
                {categories.map((category) => (
                  <MenuItem
                    key={category.id}
                    value={category.id}
                    sx={{ fontFamily: '"Saira", sans-serif' }}
                  >
                    {category.icon} {category.name} - {category.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Title */}
            <TextField
              fullWidth
              label="Discussion Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { fontFamily: '"Saira", sans-serif' },
                '& .MuiOutlinedInput-root': { fontFamily: '"Saira", sans-serif' }
              }}
              disabled={submitting}
              placeholder="What would you like to discuss?"
            />

            {/* Content */}
            <TextField
              fullWidth
              label="Discussion Description"
              multiline
              rows={8}
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              required
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': { fontFamily: '"Saira", sans-serif' },
                '& .MuiOutlinedInput-root': { fontFamily: '"Saira", sans-serif' }
              }}
              disabled={submitting}
              placeholder="Describe your discussion topic in detail..."
            />
          </Box>

          {/* Footer Actions */}
          <Box sx={{
            p: 3,
            borderTop: '1px solid rgba(0,0,0,0.1)',
            background: 'white',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              onClick={() => setOpen(false)}
              disabled={submitting}
              variant="outlined"
              sx={{ fontFamily: '"Saira", sans-serif' }}
            >
              Cancel
            </Button>
            <Button
              onClick={createThread}
              variant="contained"
              disabled={submitting || !formData.title.trim() || !formData.content.trim()}
              sx={{
                background: themeColors.primary,
                fontFamily: '"Saira", sans-serif',
                '&:hover': { background: themeColors.primaryHover }
              }}
            >
              {submitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} color="inherit" />
                  Creating...
                </Box>
              ) : (
                'Post Discussion'
              )}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Notification Snackbar */}
      <Snackbar
        open={showNotificationSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowNotificationSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowNotificationSnackbar(false)}
          severity="info"
          sx={{ width: '100%', fontFamily: '"Saira", sans-serif' }}
          icon={<NotificationsActive />}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Forums;